import { useState } from 'react'
import type { CandidateProfile } from '@/data/mockProfiles'

interface CandidateCardProps {
  candidate: CandidateProfile
  onSwipe: (direction: 'left' | 'right') => void
  style?: React.CSSProperties
  isDragging?: boolean
}

export function CandidateCard({ candidate, onSwipe, style, isDragging }: CandidateCardProps) {
  return (
    <div
      className={`w-full h-full select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={style}
    >
      <div className="h-full bg-card-white rounded-3xl shadow-depth-3 overflow-hidden flex flex-col">
        {/* Header with red gradient */}
        <div className="relative h-[76px] bg-primary-accent px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white leading-tight">{candidate.title}</h2>
          <span className="text-white/80 flex items-center gap-1 flex-shrink-0">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            {candidate.location}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-white scrollbar-hide">
          {/* Quick info - Erfaring + Tilgjengelighet */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-primary-accent">⭐</span>
                <span className="text-sm text-gray-500">Erfaring</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{candidate.experience}</p>
            </div>
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-primary-accent"></span>
                <span className="text-sm text-gray-500">Tilgjengelighet</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{candidate.availability}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-sm text-gray-700 leading-relaxed">{candidate.description}</p>
          </div>

          {/* Hovedkompetanse - Top 4 skills */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Hovedkompetanse:</h3>
            <div className="grid grid-cols-2 gap-2">
              {candidate.topSkills.slice(0, 4).map((skill, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 rounded-xl px-3 py-2">
                  <span className="text-primary-accent">✓</span>
                  <span>{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Personlighet badges */}
          <div className="flex flex-wrap gap-2 justify-center">
            {candidate.traits.map((trait, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-accent text-white flex items-center gap-1">
                <span>⭐</span> {trait}
              </span>
            ))}
          </div>

          {/* Lønnforventning (valgfritt) */}
          {candidate.salary && (
            <div className="text-center pt-2">
              <p className="text-sm text-gray-500">Lønnforventning</p>
              <p className="text-sm font-semibold text-gray-700">{candidate.salary}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

